/**
 * Confirmation before deleting every company AND every acquisition run for
 * one SIC code - the registry/acquisition-log "delete category" bulk action.
 * Mirrors CompanyProfile's DeleteCompanyDialog, scoped to a whole code
 * instead of one company: same data-protection stakes, so the same
 * discipline about naming exactly what goes, just multiplied by however many
 * companies and runs the code covers. Unlike the single-company delete, this
 * one does not leave the acquisition log behind - see the API route's own
 * header comment for why.
 */

import { useEffect, useState } from 'react';
import { IconAlert } from './Icons.jsx';
import { acquisitionApi, companiesApi } from '../lib/api.js';

export default function DeleteSicCodeDialog({ sicCode, description, onCancel, onDeleted }) {
  const [count, setCount] = useState(null);
  const [runCount, setRunCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Both counts the operator is confirming against are read live, not from
  // whatever a filter dropdown last said - those can be stale (loaded on page
  // mount, before other edits) and this is the one place being wrong costs
  // data.
  useEffect(() => {
    let cancelled = false;
    setLoadingCount(true);
    Promise.all([
      companiesApi.list({ sicCode, limit: 1 }).then((result) => result.total ?? 0),
      acquisitionApi.list({ sicCode, limit: 1 }).then((result) => result.total ?? 0),
    ])
      .then(([companies, runs]) => {
        if (cancelled) return;
        setCount(companies);
        setRunCount(runs);
      })
      .catch(() => {
        if (cancelled) return;
        setCount(null);
        setRunCount(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingCount(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sicCode]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel]);

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await companiesApi.deleteBySicCode(sicCode);
      onDeleted?.({
        companies: result.deleted ?? 0,
        runs: result.deletedRuns ?? 0,
        sicCode: result.sicCode ?? sicCode,
      });
    } catch (caught) {
      setError(caught.message ?? 'Could not delete these companies.');
      setBusy(false);
    }
  };

  const nothingToDelete = !loadingCount && count === 0 && runCount === 0;

  return (
    <div className="scrim" onClick={busy ? undefined : onCancel} role="presentation">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-sic-code-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dialog-header">
          <div>
            <h2 className="dialog-title" id="delete-sic-code-title">
              Delete everything under {sicCode}?
            </h2>
            <p className="dialog-description">
              {loadingCount ? (
                'Checking how much this affects…'
              ) : (
                <>
                  Removes <strong>{(count ?? 0).toLocaleString()}</strong> compan
                  {count === 1 ? 'y' : 'ies'} from the registry and{' '}
                  <strong>{(runCount ?? 0).toLocaleString()}</strong> acquisition run
                  {runCount === 1 ? '' : 's'} from the log
                </>
              )}
              {description ? ` (${description})` : ''} — and everything derived about the companies:
              SIC codes, financials, directors, website and email guesses. This cannot be undone from
              the app. <strong>Unlike deleting one company, the acquisition log is not kept</strong> —
              re-running this SIC code will start a fresh run, not restore what was deleted.
            </p>
          </div>
        </div>
        {nothingToDelete ? (
          <div className="dialog-body">
            <div className="alert alert-info">
              <span className="alert-body">
                Nothing is held for this code — no companies in the registry, no runs in the log.
              </span>
            </div>
          </div>
        ) : null}
        {error ? (
          <div className="dialog-body">
            <div className="alert alert-error">
              <span className="alert-icon">
                <IconAlert />
              </span>
              <span className="alert-body">{error}</span>
            </div>
          </div>
        ) : null}
        <div className="dialog-footer">
          <span className="dialog-footer-spacer" />
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => void handleConfirm()}
            disabled={busy || loadingCount || nothingToDelete}
          >
            {busy && <span className="spinner" />}
            {busy ? 'Deleting' : 'Delete everything'}
          </button>
        </div>
      </div>
    </div>
  );
}
